// Iter145 · Plant↔Forwarder booking ACL admin (platform_admin only).

import React, { useMemo, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Plus, Trash2, Check, X, Building2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling/admin/plant-grants`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

export default function AdminPlantGrants() {
  const qc = useQueryClient();
  const { data: catalog } = useQuery({
    queryKey: ["pg-catalog"],
    queryFn: () => axios.get(`${API}/plants-catalog`).then(r => r.data),
  });
  const { data: grants = [], isLoading } = useQuery({
    queryKey: ["pg-list"],
    queryFn: () => axios.get(API).then(r => r.data.items || []),
  });

  const [plantId, setPlantId] = useState("");
  const [fwdId, setFwdId] = useState("");

  const create = useMutation({
    mutationFn: (body) => axios.post(API, body).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pg-list"] }); setPlantId(""); setFwdId(""); toast.success("Hozzáférés hozzárendelve"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba"),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }) => axios.patch(`${API}/${id}`, body).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pg-list"] }),
  });
  const del = useMutation({
    mutationFn: (id) => axios.delete(`${API}/${id}`).then(() => id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pg-list"] }); toast.success("Hozzáférés törölve"); },
  });

  const plants = catalog?.plants || [];
  const tenants = catalog?.tenants || [];

  const canCreate = plantId && fwdId && plantId !== fwdId;

  return (
    <div data-testid="admin-plant-grants" className="p-1">
      <header className="mb-4">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <ShieldCheck size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Gyár ↔ Fuvarozó hozzáférések
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Platform admin · beállítja, mely fuvarozó melyik gyárba foglalhat
        </div>
      </header>

      {/* Create form */}
      <section className="mb-4 rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Plus size={14} color={BLUE} />
          <div style={syne(13, 700)}>Új hozzáférés</div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div style={mono(9, "var(--dimmer, #9198b0)", 700, "0.4px")}>GYÁR (PLANT)</div>
            <select value={plantId} onChange={(e) => setPlantId(e.target.value)} data-testid="pg-plant-select"
              className="px-2 py-1.5 rounded mt-1"
              style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12), minWidth: 260 }}
            >
              <option value="">— Válassz gyárat —</option>
              {plants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>FUVAROZÓ (BÉRLŐ)</div>
            <select value={fwdId} onChange={(e) => setFwdId(e.target.value)} data-testid="pg-forwarder-select"
              className="px-2 py-1.5 rounded mt-1"
              style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12), minWidth: 260 }}
            >
              <option value="">— Válassz fuvarozót —</option>
              {tenants.filter(t => t.id !== plantId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={() => canCreate && create.mutate({ plant_tenant_id: plantId, forwarder_tenant_id: fwdId, active: true })}
            disabled={!canCreate || create.isPending}
            data-testid="pg-add-grant"
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: canCreate ? BLUE : "rgba(148,163,184,0.15)", color: canCreate ? "#fff" : "var(--dimmer)", border: 0, cursor: canCreate ? "pointer" : "not-allowed", ...mono(11, undefined, 700, "0.4px") }}
          ><Plus size={14} /> Hozzáadás</button>
        </div>
      </section>

      {/* List */}
      {isLoading && <div style={mono(11, "var(--dimmer)")}>Betöltés…</div>}
      {!isLoading && grants.length === 0 && (
        <div className="rounded p-4" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)", ...mono(12, "var(--dimmer)") }}>
          Még nincs egy hozzáférés sem beállítva.
        </div>
      )}
      {!isLoading && grants.length > 0 && (
        <div className="rounded-md" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.20)" }}>
                {["GYÁR", "FUVAROZÓ", "AKTÍV", "LÉTREHOZVA", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grants.map(g => (
                <tr key={g.id} data-testid={`pg-grant-row-${g.id}`}
                  style={{ borderBottom: "1px solid rgba(148,163,184,0.10)", opacity: g.active ? 1 : 0.55 }}
                >
                  <td className="px-3 py-2" style={mono(12)}>{g.plant_name || g.plant_tenant_id}</td>
                  <td className="px-3 py-2" style={mono(12)}>{g.forwarder_name || g.forwarder_tenant_id}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => patch.mutate({ id: g.id, body: { active: !g.active } })}
                      data-testid={`pg-toggle-${g.id}`}
                      className="px-2 py-0.5 rounded"
                      style={{
                        background: g.active ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.08)",
                        border: `1px solid ${g.active ? "rgba(34,197,94,0.35)" : "rgba(148,163,184,0.30)"}`,
                        color: g.active ? "#22c55e" : "var(--dimmer)",
                        cursor: "pointer", ...mono(10, undefined, 700, "0.4px"),
                      }}
                    >{g.active ? "AKTÍV" : "INAKTÍV"}</button>
                  </td>
                  <td className="px-3 py-2" style={mono(11, "var(--dimmer)")}>{fmtDate(g.granted_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => window.confirm("Törli ezt a hozzáférést?") && del.mutate(g.id)}
                      data-testid={`pg-revoke-${g.id}`}
                      className="p-1 rounded"
                      title="Törlés"
                      style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                    ><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
